import React from 'react';
import { Button, Grid } from '@mui/material';
import { Form } from 'formik';
import {
    Location,
    locationActionToLabel,
    LocationButton,
    LocationButtonActions
} from 'components/location/models/LocationModel';
import { MuiForm } from 'lib/Form';
import { SortableFormField } from 'lib/form/fields/SortableFormField';
import { Row } from 'ui/Flex';
import { Panel } from 'ui/Panel';
import {
    LocationButtonForm,
    LocationButtonFormData,
    locationButtonFields
} from '../forms/LocationButtonForm';
import { locationApi } from '../LocationApi';
import { LocationButtonCardComponent } from './LocationButtonCardComponent';

export interface LocationButtonCardProps {
    location: Location;
    setCurrentLocation: (location: Location) => void;
}

interface LocationButtonFormValues {
    buttons: LocationButtonFormData[];
}

export const LocationButtonCard: React.FC<LocationButtonCardProps> = ({ location, setCurrentLocation }) => {
    const [isEditing, setIsEditing] = React.useState(false);

    const initialValues: LocationButtonFormValues = React.useMemo(() => {
        const buttons =
            location.buttons?.map((button: LocationButton) => ({
                ...button,
                actionLabel: locationActionToLabel[button.action],
                properties: button.properties ?? { url: '' }
            })) ?? [];
        return { buttons };
    }, [location.buttons]);

    const handleSubmit = React.useCallback(
        async (values: LocationButtonFormValues) => {
            const buttons = values.buttons.map((button: LocationButtonFormData) => ({
                title: button.title,
                action: button.action as LocationButtonActions,
                style: button.style as 'PRIMARY' | 'SECONDARY',
                properties: button.properties ?? { url: '' }
            })) as LocationButton[];
            const response = await locationApi.update(location._id, {
                body: { buttons }
            });
            if (response.ok) {
                setCurrentLocation(response.body);
            }
            setIsEditing(false);
        },
        [location._id, setCurrentLocation]
    );

    const toggleEditing = React.useCallback(() => {
        setIsEditing(prev => !prev);
    }, []);

    return (
        <Panel title="In App Location Buttons" showAction={!isEditing} onClick={toggleEditing} divider>
            {isEditing ? (
                <MuiForm<LocationButtonFormValues>
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ submitForm, isSubmitting }) => (
                        <Form>
                            <Grid container spacing={2}>
                                <Grid size={12}>
                                    <SortableFormField
                                        name="buttons"
                                        fields={locationButtonFields}
                                        FormComponent={LocationButtonForm}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <Row gutter align="flex-end">
                                        <Button onClick={toggleEditing} variant="outlined">
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={submitForm}
                                            disabled={isSubmitting}
                                            variant="contained"
                                            color="primary"
                                        >
                                            Submit
                                        </Button>
                                    </Row>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </MuiForm>
            ) : (
                <LocationButtonCardComponent location={location} />
            )}
        </Panel>
    );
};
